import React, { useState } from "react";
import { Banknote, Landmark, Phone, ScrollText, Wallet } from "lucide-react";
import { useWithdrawals } from "../../features/reseller/useWithdrawals";
import { WithdrawalRequestModal } from "../modals/WithdrawalRequestModal";
import { WithdrawalStatusBadge } from "../shared/StatusBadges";

/* -----------------------------------------------------------------------------
 * ResellerWithdrawals — módulo independiente visible en la pestaña "Saldo a
 * Favor" solo para revendedores: botón "Retirar Saldo" + historial propio.
 * No comparte estado ni lógica con pedidos/pagos.
 * --------------------------------------------------------------------------- */
export function ResellerWithdrawals({ user, showToast, refreshUser }) {
  const { withdrawals, reload } = useWithdrawals({ user, showToast });
  const [modalOpen, setModalOpen] = useState(false);

  if (!user || user.role !== "reseller") return null;

  const handleSuccess = async () => {
    await Promise.all([reload(), refreshUser?.()]);
  };

  return (
    <div className="space-y-4" data-testid="reseller-withdrawals-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-heading font-black text-white uppercase tracking-wide">Retiros de Revendedor</h4>
          <p className="text-[11px] text-[var(--ag7-text-muted)]">Retiro mínimo Bs 500 · Comisión fija 3.5%</p>
          {Number(user.locked_balance_bs || 0) > 0 && (
            <p className="text-[11px] text-amber-400 mt-1" data-testid="reseller-locked-balance">
              Bs {Number(user.locked_balance_bs).toFixed(2)} bloqueados en retiros pendientes
            </p>
          )}
        </div>
        <button
          type="button"
          data-testid="withdraw-balance-btn"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:opacity-90 text-white font-bold px-5 py-3 rounded-xl text-xs shadow-lg shadow-emerald-600/25 transition-opacity"
        >
          <Wallet className="w-4 h-4" /> Retirar Saldo
        </button>
      </div>

      {withdrawals.length > 0 && (
        <div className="space-y-2" data-testid="withdrawal-history-list">
          {withdrawals.map((w) => (
            <div key={w.id} data-testid={`withdrawal-row-${w.id}`} className="p-4 rounded-2xl bg-ag7-bg/80 border border-[var(--ag7-border-soft)] flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
                  <Banknote className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs font-mono text-zinc-400">{w.code}</p>
                  <p className="text-sm font-bold text-white" data-testid={`withdrawal-net-${w.id}`}>Bs {Number(w.netBs).toFixed(2)} <span className="text-[10px] text-zinc-500 font-normal">(neto de Bs {Number(w.amountBs).toFixed(2)})</span></p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-zinc-500">
                <span className="flex items-center gap-1"><Landmark className="w-3 h-3" />{w.banco}</span>
                <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{w.telefono}</span>
                <WithdrawalStatusBadge status={w.status} />
              </div>
              {w.bankReference && (
                <p className="w-full text-[10px] font-mono text-zinc-500 flex items-center gap-1"><ScrollText className="w-3 h-3" />Referencia: {w.bankReference}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <WithdrawalRequestModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        availableBs={user.credit_balance_bs}
        onSuccess={handleSuccess}
        showToast={showToast}
      />
    </div>
  );
}
